// FILE: src/pages/info/HelpPages.jsx
// Help centre and help articles (MTO 6.6), public (/help) and in-course
// (/student/help).
import React from "react";
import { Alert, Box, Button, Card, CardContent, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import InfoPage from "./InfoPage";
import HelpCentre from "../../components/help/HelpCentre";
import SimpleText from "../../components/help/SimpleText";
import useAsync from "../../components/help/useAsync";
import { getHelpArticle } from "../../services/courseInfo";
import { ErrorState, LoadingState } from "../../components/ui";

const base = (variant) => (variant === "student" ? "/student/help" : "/help");

// Public paths in article text → their in-course equivalents for students.
const STUDENT_PATHS = {
  "/help": "/student/help",
  "/glossary": "/student/glossary",
  "/system-requirements": "/student/system-requirements",
  "/accessibility": "/student/accessibility",
  "/contact": "/student/support",
  "/support": "/student/support",
  "/profile": "/student/profile",
  "/code-of-conduct": "/student/course-info/code-of-conduct",
  "/syllabus": "/student/course-info/syllabus"
};
export function articleLink(variant) {
  if (variant !== "student") return (href) => href;
  return (href) => {
    if (href.startsWith("/student")) return href;
    const hit = Object.keys(STUDENT_PATHS).find((p) => href === p || href.startsWith(`${p}/`));
    return hit ? STUDENT_PATHS[hit] + href.slice(hit.length) : href;
  };
}
const supportLink = (variant) => (variant === "student" ? "/student/support/new" : "/contact");

export function HelpCentrePage({ variant = "public" }) {
  return (
    <InfoPage
      variant={variant}
      title="Help centre"
      description="Answers to common questions, step-by-step guides and fixes for common problems."
    >
      <HelpCentre basePath={base(variant)} />
      <Card sx={{ mt: 4, bgcolor: "zun.primaryTint", borderColor: "transparent" }}>
        <CardContent>
          <Typography component="h2" variant="h3" sx={{ mb: 0.5 }}>
            Still stuck?
          </Typography>
          <Typography sx={{ mb: 1.5 }}>Real people answer every request within 48 hours.</Typography>
          <Button component={RouterLink} to={supportLink(variant)} variant="contained">
            {variant === "student" ? "Ask for help" : "Contact us"}
          </Button>
        </CardContent>
      </Card>
    </InfoPage>
  );
}

export function HelpArticlePage({ variant = "public" }) {
  const { id } = useParams();
  const { data, error, loading, reload } = useAsync(() => getHelpArticle(id), [id]);
  const article = data?.article || data;
  return (
    <InfoPage
      variant={variant}
      title={article?.title || "Help article"}
      description={article?.summary}
      breadcrumbs={[
        { label: "Help centre", to: base(variant) },
        ...(article?.categoryTitle ? [{ label: article.categoryTitle }] : []),
        { label: article?.title || "Article" }
      ]}
    >
      {loading && !data ? (
        <LoadingState variant="inline" label="Loading the article…" />
      ) : error ? (
        <ErrorState error={error} title="We couldn't open this article" onRetry={reload} />
      ) : (
        <Stack spacing={3} sx={{ maxWidth: "72ch" }}>
          <Box component="article">
            <SimpleText text={article.body} headingLevel={2} linkTo={articleLink(variant)} />
          </Box>
          {article.related?.length > 0 && (
            <Box component="section" aria-labelledby="related-title">
              <Typography id="related-title" variant="h2" sx={{ fontSize: "1.25rem", mb: 1 }}>
                Related articles
              </Typography>
              <Stack component="ul" spacing={0.75} sx={{ pl: 3, my: 0 }}>
                {article.related.map((r) => (
                  <li key={r.id}>
                    <Link component={RouterLink} to={`${base(variant)}/${r.id}`}>
                      {r.title}
                    </Link>
                  </li>
                ))}
              </Stack>
            </Box>
          )}
          <Alert severity="info">
            Didn't solve it?{" "}
            <Link component={RouterLink} to={supportLink(variant)}>
              {variant === "student" ? "Ask student support" : "Contact us"}
            </Link>{" "}
            and we'll reply within 48 hours.
          </Alert>
          <Box>
            <Link component={RouterLink} to={base(variant)}>
              Back to the help centre
            </Link>
          </Box>
        </Stack>
      )}
    </InfoPage>
  );
}
